import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  Tree,
  TreeItem,
  TreeItemLayout,
  Spinner,
  ProgressBar,
  Field,
} from "@fluentui/react-components";
import { FolderAddRegular, FolderArrowRightRegular, FolderOpenRegular, FolderRegular } from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFolderSearch } from "./useFolderSearch";
import FolderSearchBox from "./FolderSearchBox";
import FolderSearchResults from "./FolderSearchResults";

import useAuthContext from "../../../hooks/useAuthContext";
import useMessageContext from "../../../hooks/useMessageContext";
import { getFolders, fileItems, createFolder } from "../../../api/file";
import { MailFolder } from "../../../types/filing";
import {
  browseContainerClass,
  treeContainerClass,
  treeItemLayoutClass,
  treeItemLayoutHighlightClass,
  labelClass,
  selectedLabelClass,
  footerClass,
  fileButtonClass,
  newFolderInputClass,
  newFolderErrorClass,
} from "./styles";

const EXCLUDED_FOLDERS = ["Drafts", "Outbox", "Junk Email", "RSS Feeds", "Snoozed", "Sent Items","Sync Issues", "Deleted Items", "Sent", "Conversation History"];

type FolderNodeProps = {
  folder: MailFolder;
  selectedId: string | null;
  onSelect: (id: string, name: string, path?: string) => void;
  openSiblingId: string | null;
  onOpenChange: (id: string | null) => void;
  parentPath?: string;
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  selectedId,
  onSelect,
  openSiblingId,
  onOpenChange,
  parentPath = "",
}) => {
  const myPath = parentPath ? `${parentPath}/${folder.displayName}` : folder.displayName;
  const isOpen = openSiblingId === folder.id;
  const [openChildId, setOpenChildId] = useState<string | null>(null);
  const [hasCreatedChild, setHasCreatedChild] = useState(false);

  const { data: children, isFetching } = useQuery({
    queryKey: ["graphFolders", folder.id],
    queryFn: () => getFolders(folder.id),
    enabled: isOpen && (folder.childFolderCount > 0 || hasCreatedChild),
    staleTime: 60_000,
  });

  const hasChildren = folder.childFolderCount > 0 || hasCreatedChild;
  const isSelected = selectedId === folder.id;

  const [showProgress, setShowProgress] = useState(false);
  useEffect(() => {
    if (!(isOpen && hasChildren && isFetching && !children)) {
      setShowProgress(false);
      return;
    }
    const t = setTimeout(() => setShowProgress(true), 400);
    return () => clearTimeout(t);
  }, [isOpen, hasChildren, isFetching, children]);

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const contextMenuTarget = useRef<{ getBoundingClientRect: () => DOMRect } | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderError, setFolderError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutateAsync: doCreateFolder, isPending: isCreating } = useMutation({
    mutationFn: (name: string) => createFolder(folder.id, name),
    onSuccess: (created) => {
      setHasCreatedChild(true);
      queryClient.invalidateQueries({ queryKey: ["graphFolders", folder.id] });
      if (!isOpen) onOpenChange(folder.id);
      onSelect(created.id, created.displayName, `${myPath}/${created.displayName}`);
      setIsDialogOpen(false);
      setNewFolderName("");
      setFolderError(null);
    },
    onError: (err: unknown) => {
      setFolderError(err instanceof Error ? err.message : "Failed to create folder.");
    },
  });

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    contextMenuTarget.current = { getBoundingClientRect: () => new DOMRect(e.clientX, e.clientY, 0, 0) };
    setIsContextMenuOpen(true);
  };

  const handleNewFolderClick = () => {
    setIsContextMenuOpen(false);
    setNewFolderName("");
    setFolderError(null);
    setIsDialogOpen(true);
  };

  const handleDialogSubmit = () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) { setFolderError("Folder name cannot be empty."); return; }
    void doCreateFolder(trimmed);
  };

  const handleDialogCancel = () => {
    setIsDialogOpen(false);
    setNewFolderName("");
    setFolderError(null);
  };

  return (
    <>
      <Menu
        open={isContextMenuOpen}
        onOpenChange={(_, data) => setIsContextMenuOpen(data.open)}
        positioning={{ target: contextMenuTarget.current }}
      >
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<FolderAddRegular />} onClick={handleNewFolderClick}>
              New Folder
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      <TreeItem
        itemType={hasChildren ? "branch" : "leaf"}
        open={isOpen}
        onOpenChange={(_, data) => {
          onOpenChange(data.open ? folder.id : null);
          if (!data.open) setOpenChildId(null);
        }}
        value={folder.id}
        onContextMenu={handleContextMenu}
      >
        <TreeItemLayout
          iconBefore={isOpen ? <FolderOpenRegular /> : <FolderRegular />}
          onClick={() => onSelect(folder.id, folder.displayName, myPath)}
          className={`${treeItemLayoutClass} ${isContextMenuOpen || isDialogOpen ? treeItemLayoutHighlightClass : ""}`}
          title={folder.displayName}
        >
          <span className={isSelected ? selectedLabelClass : labelClass}>
            {folder.displayName}
          </span>
        </TreeItemLayout>

        {showProgress && (
          <ProgressBar thickness="medium" style={{ margin: "4px 0 4px 28px" }} />
        )}

        {isOpen && hasChildren && children && (
          <Tree>
            {children
              .filter((c) => !EXCLUDED_FOLDERS.includes(c.displayName))
              .map((child) => (
                <FolderNode
                  key={child.id}
                  folder={child}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  openSiblingId={openChildId}
                  onOpenChange={setOpenChildId}
                  parentPath={myPath}
                />
              ))}
          </Tree>
        )}
      </TreeItem>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(_, data) => { if (!data.open) handleDialogCancel(); }}
        modalType="modal"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New Folder</DialogTitle>
            <DialogContent>
              <Input
                value={newFolderName}
                onChange={(_, data) => setNewFolderName(data.value)}
                placeholder={`Subfolder of "${folder.displayName}"`}
                disabled={isCreating}
                autoFocus
                className={newFolderInputClass}
                onKeyDown={(e) => { if (e.key === "Enter") handleDialogSubmit(); }}
              />
              {folderError && <div className={newFolderErrorClass}>{folderError}</div>}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={handleDialogSubmit}
                disabled={isCreating}
                icon={isCreating ? <Spinner size="tiny" /> : undefined}
              >
                {isCreating ? "Creating…" : "Create"}
              </Button>
              <Button onClick={handleDialogCancel} disabled={isCreating}>
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

type FileBrowseProps = {
  onFiled?: (folder: { graphFolderId: string; graphFolderName: string; graphFolderPath: string }) => void;
  itemIds: string[];
};

const FileBrowse: React.FC<FileBrowseProps> = ({ onFiled, itemIds }) => {
  const { tokenData } = useAuthContext();
  const { handleShowMessageBar } = useMessageContext();
  const msUserId = tokenData.oid;

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null);
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>("");

  const handleSelectFolder = (id: string, name: string, path = "") => {
    setSelectedFolderId(id);
    setSelectedFolderName(name);
    setSelectedFolderPath(path);
  };
  const [openRootId, setOpenRootId] = useState<string | null>(null);

  const [isRootContextMenuOpen, setIsRootContextMenuOpen] = useState(false);
  const rootContextMenuTarget = useRef<{ getBoundingClientRect: () => DOMRect } | null>(null);
  const [isRootDialogOpen, setIsRootDialogOpen] = useState(false);
  const [rootFolderName, setRootFolderName] = useState("");
  const [rootFolderError, setRootFolderError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutateAsync: doCreateRootFolder, isPending: isCreatingRoot } = useMutation({
    mutationFn: (name: string) => createFolder(null, name),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["graphFolders", "root"] });
      handleSelectFolder(created.id, created.displayName, created.displayName);
      setIsRootDialogOpen(false);
      setRootFolderName("");
      setRootFolderError(null);
    },
    onError: (err: unknown) => {
      setRootFolderError(err instanceof Error ? err.message : "Failed to create folder.");
    },
  });

  const handleRootContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("[role='treeitem']")) return;
    e.preventDefault();
    rootContextMenuTarget.current = { getBoundingClientRect: () => new DOMRect(e.clientX, e.clientY, 0, 0) };
    setIsRootContextMenuOpen(true);
  };

  const handleRootNewFolderClick = () => {
    setIsRootContextMenuOpen(false);
    setRootFolderName("");
    setRootFolderError(null);
    setIsRootDialogOpen(true);
  };

  const handleRootDialogSubmit = () => {
    const trimmed = rootFolderName.trim();
    if (!trimmed) { setRootFolderError("Folder name cannot be empty."); return; }
    void doCreateRootFolder(trimmed);
  };

  const handleRootDialogCancel = () => {
    setIsRootDialogOpen(false);
    setRootFolderName("");
    setRootFolderError(null);
  };
  const [searchTerm, setSearchTerm] = useState("");
  const treeScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedFolderId) return;
    const el = treeScrollRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth) {
      el.scrollLeft = el.scrollWidth;
    }
  }, [selectedFolderId]);

  const { data: rootFolders, isPending } = useQuery({
    queryKey: ["graphFolders", "root"],
    queryFn: () => getFolders(),
    staleTime: 60_000,
  });

  const { mutateAsync: doFile, isPending: isFiling } = useMutation({
    mutationFn: () => fileItems(msUserId, itemIds, selectedFolderId!),
    onSuccess: () => {
      handleShowMessageBar(
        `${itemIds.length === 1 ? "Message" : `${itemIds.length} messages`} filed to "${selectedFolderName}".`,
        false
      );
      onFiled?.({
        graphFolderId: selectedFolderId!,
        graphFolderName: selectedFolderName!,
        graphFolderPath: selectedFolderPath,
      });
    },
  });

  const visibleRootFolders = (rootFolders ?? [])
    .filter((f) => !EXCLUDED_FOLDERS.includes(f.displayName))
    .sort((a, b) => {
      if (a.displayName === "Inbox") return -1;
      if (b.displayName === "Inbox") return 1;
      return 0;
    });

  const isSearching = searchTerm.trim().length > 0;
  const { results: searchResults, isLoading: searchIsLoading } = useFolderSearch(
    visibleRootFolders,
    searchTerm
  );

  if (isPending) {
    return (
      <Field validationMessage="Loading folders..." validationState="none">
        <ProgressBar />
      </Field>
    );
  }

  return (
    <div className={browseContainerClass}>
      <FolderSearchBox value={searchTerm} onChange={setSearchTerm} />
      <Menu
        open={isRootContextMenuOpen}
        onOpenChange={(_, data) => setIsRootContextMenuOpen(data.open)}
        positioning={{ target: rootContextMenuTarget.current }}
      >
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<FolderAddRegular />} onClick={handleRootNewFolderClick}>
              New Folder
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      <div className={treeContainerClass} ref={treeScrollRef} onContextMenu={handleRootContextMenu}>
        {isSearching ? (
          <FolderSearchResults
            results={searchResults}
            selectedId={selectedFolderId}
            onSelect={handleSelectFolder}
            isLoading={searchIsLoading}
          />
        ) : (
          <Tree aria-label="Outlook folders">
            {visibleRootFolders.map((folder) => (
              <FolderNode
                key={folder.id}
                folder={folder}
                selectedId={selectedFolderId}
                onSelect={handleSelectFolder}
                openSiblingId={openRootId}
                onOpenChange={setOpenRootId}
              />
            ))}
          </Tree>
        )}
      </div>

      <Dialog
        open={isRootDialogOpen}
        onOpenChange={(_, data) => { if (!data.open) handleRootDialogCancel(); }}
        modalType="modal"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New Folder</DialogTitle>
            <DialogContent>
              <Input
                value={rootFolderName}
                onChange={(_, data) => setRootFolderName(data.value)}
                placeholder="Top-level folder name"
                disabled={isCreatingRoot}
                autoFocus
                className={newFolderInputClass}
                onKeyDown={(e) => { if (e.key === "Enter") handleRootDialogSubmit(); }}
              />
              {rootFolderError && <div className={newFolderErrorClass}>{rootFolderError}</div>}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={handleRootDialogSubmit}
                disabled={isCreatingRoot}
                icon={isCreatingRoot ? <Spinner size="tiny" /> : undefined}
              >
                {isCreatingRoot ? "Creating…" : "Create"}
              </Button>
              <Button onClick={handleRootDialogCancel} disabled={isCreatingRoot}>
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <div className={footerClass}>
        <Button
          appearance="primary"
          className={fileButtonClass}
          disabled={!selectedFolderId || isFiling}
          icon={isFiling ? <Spinner size="tiny" /> : <FolderArrowRightRegular />}
          onClick={() => void doFile()}
        >
          {isFiling ? "Filing…" : itemIds.length > 1 ? `File ${itemIds.length} Messages` : "File"}
        </Button>
      </div>
    </div>
  );
};

export default FileBrowse;
